import { IntlShape } from 'react-intl';

export interface DateRange {
  from: Date;
  to: Date;
}

export const getNextDay = (date: Date | string | number) => {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  nextDay.setHours(0, 0, 0, 0);
  return nextDay;
};

export const getPreviousDay = (date: Date | string | number) => {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() - 1);
  nextDay.setHours(0, 0, 0, 0);
  return nextDay;
};

export const getPreviousUTCDay = (date: Date | string | number) => {
  const nextDay = new Date(date);
  nextDay.setUTCDate(nextDay.getUTCDate() - 1);
  nextDay.setUTCHours(0, 0, 0, 0);
  return nextDay;
};

export const addMinutesToDate = (
  date: Date | string | number,
  minutes: number
) => {
  const updatedDate = new Date(date);
  updatedDate.setMinutes(updatedDate.getMinutes() + minutes);
  return updatedDate;
};

export const resetUTCHours = (date: Date) => {
  const updatedDate = new Date(date);
  // To avoid going one day backwards
  updatedDate.setUTCDate(date.getDate());
  // To avoid going one month backwards on first day of month
  updatedDate.setUTCMonth(date.getMonth());
  updatedDate.setUTCFullYear(date.getFullYear());
  // To avoid going one year backwards on first day of year
  updatedDate.setUTCHours(0, 0, 0, 0);
  return updatedDate;
};

export const isOlderThanOneDay = (date1: Date, date2: Date) => {
  const dayMs = 1000 * 60 * 60 * 24;
  return Date.parse(date2.toString()) - Date.parse(date1.toString()) >= dayMs;
};

export const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Converts to format yyyy-MM-dd
 */
export const convertToApiDate = (date: Date) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${date.getFullYear()}-${month < 10 ? `0${month}` : month}-${
    day < 10 ? `0${day}` : day
  }`;
};

/**
 * Converts to format yyyy-MM-dd
 */
export const convertToUTCApiDate = (date: Date) => {
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  return `${date.getUTCFullYear()}-${month < 10 ? `0${month}` : month}-${
    day < 10 ? `0${day}` : day
  }`;
};

/**
 * Api returns 2 millisecond integers, whereas js adds 3 ms integers
 */
export const toApiIsoDate = (date: Date) => {
  const isoDate = date.toISOString();
  return `${isoDate.slice(0, isoDate.length - 2)}Z`;
};

export const getDurationMinutes = (start: Date, end: Date) => {
  return Math.floor((end.getTime() - start.getTime()) / 1000 / 60);
};

export const getHoursAndMinutes = (mins: number, intl: IntlShape) => {
  const hours = Math.floor(mins / 60);
  const remainingMinutes = mins % 60;

  const hoursSection = intl.formatNumber(hours, {
    style: 'unit',
    unit: 'hour',
  });
  const minutesSection = intl.formatNumber(remainingMinutes, {
    style: 'unit',
    unit: 'minute',
  });

  if (hours < 1 && remainingMinutes > 0) {
    return minutesSection;
  }

  if (hours >= 1 && remainingMinutes > 0) {
    return `${hoursSection} ${minutesSection}`;
  }

  if (hours >= 1) {
    return hoursSection;
  }

  return '';
};

export const dateRangeToArray = (range: DateRange): [Date, Date] => {
  return [range.from, range.to];
};

export const isDateInRange = ({
  dateMs,
  startDateMs,
  endDateMs,
}: {
  dateMs: number;
  startDateMs: number;
  endDateMs: number;
}) => {
  return dateMs > startDateMs && dateMs < endDateMs;
};

export const doDateRangesOverlap = (
  range1: { from: string; to: string },
  range2: { from: string; to: string }
) => {
  return (
    (range1.from > range2.from && range1.from < range2.to) ||
    (range2.from > range1.from && range2.from < range1.to)
  );
};
